/**
 * The one-request-and-a-redirect page — `/@handle`, `/c/NAME`, `/user/NAME`, `/clip/ID`.
 *
 * Everything here is written around a page that exists for one frame if it succeeds, so the only copy
 * that gets read is the *failure* copy. Two of those sentences are the reason the section exists at
 * all: a malformed clip id comes back as a `200` with **zero bytes**, which is a statement about the
 * link rather than about the instance — the shared `malformed-json` sentence would send a reader off to
 * check their network for a mistyped id.
 */
export const resolve = {
  /**
   * The two lines the page shows while it is doing its one job. Both were JSX text in `view.tsx`, which
   * is how a page that exists for one frame ends up being the last one anybody notices is untranslated.
   */
  found: 'Found it. Opening…',
  lookingUp: (label: string): string => `Looking up ${label}…`,

  subject: {
    channelName: 'A channel given by name, not by id. Looking it up on the instance.',
    clipLabel: 'this clip',
    clip:
      "A clip is a section of somebody else's video. Visious opens the video at the point the clip starts.",
    linkLabel: 'this link',
    link: 'Looking it up on the instance.',
    /** `@handle` keeps its sigil; a `/c/NAME` does not have one. */
    handle: (name: string): string => `@${name}`,
  },

  notResolvable: {
    title: 'That link cannot be looked up',
    detail: 'Visious does not know how to open a link of this kind.',
    retryable: false,
  },

  channelName: {
    notFoundTitle: (label: string): string => `No channel called ${label}`,
    notFoundDetail:
      'YouTube says there is nothing there. A handle that has been changed reads the same way as one that never existed.',
    unreadableTitle: (label: string): string => `Could not read the answer for ${label}`,
    unreadableDetail: 'The instance replied with something that does not name a channel.',
    upstreamBrokenTitle: (label: string): string => `Could not look up ${label}`,
    upstreamBrokenDetail:
      'The lookup failed on the instance, which is not the same as finding nothing. Trying again gets the same answer.',
  },

  clip: {
    notFound: {
      title: 'That clip is gone',
      detail:
        'A clip depends on its video: if the video was deleted or made private, the clip stops working even though the link still looks fine.',
      retryable: false,
    },
    unreadableTitle: 'Could not read this clip',
    unreadableDetail: 'The instance replied with something Visious could not read as a clip.',
  },

  /**
   * The `unrecognised` arm, which has to say what happened rather than "not found": the instance
   * answered `200`, and an unknown URL resolves to `FEwhat_to_watch` — YouTube's own home feed — so a
   * successful request is not a successful resolution. `pageType` is what came back, and
   * `unknownPageType` stands in when the target carried none.
   */
  unrecognisedTitle: (label: string): string => `The instance did not recognise ${label}`,
  unrecognisedDetail: (pageType: string): string =>
    `It answered successfully with a page type it could not turn into anything to open (${pageType}). For an unknown name that is what YouTube returns instead of an error.`,
  unknownPageType: 'an unknown shape',

  /** The zero-byte `200`, which is a sentence about the link rather than about the connection. */
  notFollowedTitle: (label: string): string => `The instance could not follow ${label}`,
  notFollowedDetail:
    'It answered with nothing at all, which is how it turns down an id it cannot read. Check the link for a truncated or partly-copied id.',
} as const;
